import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, Package, ShoppingCart, FileText, Users, 
  Settings, LogOut, TrendingUp, Plus, Edit, Trash2, Eye, 
  Search, Filter, Download, Clock, CheckCircle, XCircle, Truck, Tag,
  Flame, Camera
} from 'lucide-react';
import AdminLogin from './components/AdminLogin';
import Dashboard from './components/Dashboard';
import ProductManagement from './components/ProductManagement';
import OrderManagement from './components/OrderManagement';
import BlogManagement from './components/BlogManagement';
import CustomerManagement from './components/CustomerManagement';
import SettingsPanel from './components/SettingsPanel';
import DiscountManagement from './components/DiscountManagement';
import DropManagement from './components/DropManagement';
import LookbookManagement from './components/LookbookManagement';

const AdminApp = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  const [adminUser, setAdminUser] = useState(null);

  useEffect(() => {
    // Check for existing token
    const token = localStorage.getItem('adminToken');
    const user = localStorage.getItem('adminUser');
    
    if (token && user) {
      setIsAuthenticated(true);
      setAdminUser(JSON.parse(user));
    }
  }, []);

  const handleLogin = (token, user) => {
    localStorage.setItem('adminToken', token);
    localStorage.setItem('adminUser', JSON.stringify(user));
    setIsAuthenticated(true);
    setAdminUser(user);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setIsAuthenticated(false);
    setAdminUser(null);
    setCurrentView('dashboard');
  };

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
    { id: 'products', label: 'Products', icon: <Package size={20} /> },
    { id: 'drops', label: 'Drops', icon: <Flame size={20} /> },
    { id: 'orders', label: 'Orders', icon: <ShoppingCart size={20} /> },
    { id: 'discounts', label: 'Discounts', icon: <Tag size={20} /> },
    { id: 'blogs', label: 'Blog', icon: <FileText size={20} /> },
    { id: 'lookbooks', label: 'Lookbooks', icon: <Camera size={20} /> },
    { id: 'customers', label: 'Customers', icon: <Users size={20} /> },
    { id: 'settings', label: 'Settings', icon: <Settings size={20} /> },
  ];

  const renderView = () => {
    switch(currentView) {
      case 'dashboard':
        return <Dashboard />;
      case 'products':
        return <ProductManagement />;
      case 'drops':
        return <DropManagement />;
      case 'orders':
        return <OrderManagement />;
      case 'discounts':
        return <DiscountManagement />;
      case 'blogs':
        return <BlogManagement />;
      case 'lookbooks':
        return <LookbookManagement />;
      case 'customers':
        return <CustomerManagement />;
      case 'settings':
        return <SettingsPanel />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-black text-white flex flex-col border-r-4 border-yellow-400">
        {/* Logo */}
        <div className="p-4 border-b-4 border-yellow-400 flex flex-col items-center">
          <img src="/owlfit-logo.png" alt="OWLFIT" className="w-full max-w-[180px] object-contain" draggable="false" />
          <div className="text-[10px] font-mono mt-1 text-yellow-400 tracking-widest">ADMIN_CONSOLE_V1.0</div>
        </div>

        {/* Navigation */}
        <nav className="flex-grow p-4 space-y-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setCurrentView(item.id)}
              className={`
                w-full flex items-center gap-3 px-4 py-3 font-bold uppercase text-sm
                transition-all border-2 
                ${currentView === item.id 
                  ? 'bg-yellow-400 text-black border-yellow-400 shadow-[4px_4px_0px_rgba(255,255,255,0.2)]' 
                  : 'border-transparent hover:border-yellow-400 hover:bg-gray-900'
                }
              `}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t-4 border-yellow-400">
          <div className="mb-3 p-3 bg-gray-900 border border-gray-700">
            <div className="text-xs text-gray-400 uppercase tracking-wide">Logged in as</div>
            <div className="font-bold text-sm truncate">{adminUser?.email}</div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-500 font-bold uppercase text-sm border-2 border-red-700 shadow-[4px_4px_0px_rgba(0,0,0,0.5)] transition-all"
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-grow overflow-y-auto">
        {renderView()}
      </main>
    </div>
  );
};

export default AdminApp;
